import { useState, useEffect, useRef } from "react";
import { authFetch } from "../api";

export default function SiteAssignments({ centers, apiBase, onClose }) {
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [email, setEmail]             = useState("");
  const [centerId, setCenterId]       = useState("");
  const [saving, setSaving]           = useState(false);
  const [notice, setNotice]           = useState(null);
  const [userSearch, setUserSearch]   = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef                   = useRef(null);

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const [assignRes, userRes] = await Promise.all([
        authFetch(`${apiBase}/admin/assignments`),
        authFetch(`${apiBase}/admin/users`),
      ]);
      const assignData = await assignRes.json();
      const userData   = await userRes.json();
      setAssignments(Array.isArray(assignData) ? assignData : []);
      setUsers(Array.isArray(userData) ? userData : []);
    } catch {
      showNotice("error", "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAssignments(); }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const showNotice = (type, text) => {
    setNotice({ type, text });
    setTimeout(() => setNotice(null), 3500);
  };

  const handleAssign = async () => {
    if (!email.trim() || !centerId) return;
    setSaving(true);
    try {
      console.log("Assigning", email.trim(), "to", centerId, "via", apiBase);
      const res = await authFetch(`${apiBase}/admin/assignments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_email: email.trim(), center_id: centerId }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || res.statusText);
      }
      setEmail("");
      setUserSearch("");
      setCenterId("");
      showNotice("ok", "User assigned to site");
      fetchAssignments();
    } catch (e) {
      console.error("Assignment error:", e);
      showNotice("error", `Failed to assign user: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (userEmail) => {
    if (!window.confirm(`Remove assignment for ${userEmail}?`)) return;
    try {
      await authFetch(`${apiBase}/admin/assignments/${encodeURIComponent(userEmail)}`, { method: "DELETE" });
      showNotice("ok", "Assignment removed");
      fetchAssignments();
    } catch {
      showNotice("error", "Failed to remove");
    }
  };

  const centerName = (id) => centers.find(c => c.id === id)?.name ?? id;

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>

        <div style={styles.header}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Manage Site Assignments</div>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        {notice && (
          <div style={{
            ...styles.notice,
            background: notice.type === "ok" ? "#e0f3fc" : "#fdecea",
            color:      notice.type === "ok" ? "#004d74" : "#8B2020",
          }}>
            {notice.text}
          </div>
        )}

        {/* Add assignment form */}
        <div style={styles.form}>
          <div style={styles.formLabel}>Assign admin user to a site</div>
          <div ref={dropdownRef} style={{ position: "relative" }}>
            <input
              type="text"
              placeholder={email ? users.find(u => u.email === email)?.name || email : "Search user…"}
              value={userSearch}
              onChange={e => { setUserSearch(e.target.value); setDropdownOpen(true); }}
              onFocus={() => setDropdownOpen(true)}
              style={{
                ...styles.input,
                color: !userSearch && email ? "#1a1a1a" : undefined,
              }}
            />
            {dropdownOpen && (
              <div style={styles.dropdownList}>
                {users
                  .filter(u => {
                    const q = userSearch.toLowerCase();
                    return !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
                  })
                  .map(u => (
                    <div
                      key={u.email}
                      onMouseDown={() => {
                        setEmail(u.email);
                        setUserSearch("");
                        setDropdownOpen(false);
                      }}
                      style={{
                        ...styles.dropdownItem,
                        background: u.email === email ? "#e8f4fd" : undefined,
                      }}
                    >
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{u.name}</div>
                      {u.name !== u.email && <div style={{ fontSize: 12, color: "#888" }}>{u.email}</div>}
                    </div>
                  ))
                }
                {users.filter(u => {
                  const q = userSearch.toLowerCase();
                  return !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
                }).length === 0 && (
                  <div style={{ padding: "10px 12px", fontSize: 13, color: "#888" }}>No users found</div>
                )}
              </div>
            )}
          </div>
          <select
            value={centerId}
            onChange={e => setCenterId(e.target.value)}
            style={styles.input}
          >
            <option value="">— Select cooling center —</option>
            {centers.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <button
            onClick={handleAssign}
            disabled={!email.trim() || !centerId || saving}
            style={styles.assignBtn(!email.trim() || !centerId || saving)}
          >
            {saving ? "Saving…" : "Assign"}
          </button>
        </div>

        {/* Existing assignments */}
        <div style={styles.sectionLabel}>Current Assignments</div>
        {loading ? (
          <div style={{ color: "#888", fontSize: 13, padding: "8px 0" }}>Loading…</div>
        ) : assignments.length === 0 ? (
          <div style={{ color: "#888", fontSize: 13, padding: "8px 0" }}>No assignments yet.</div>
        ) : (
          <div style={styles.list}>
            {assignments.map(a => (
              <div key={a.user_email} style={styles.row}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{a.user_email}</div>
                  <div style={{ fontSize: 12, color: "#0093dd", marginTop: 2 }}>{centerName(a.center_id)}</div>
                </div>
                <button onClick={() => handleRemove(a.user_email)} style={styles.removeBtn}>
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: 16,
  },
  card: {
    background: "#fff", borderRadius: 16,
    width: "100%", maxWidth: 480,
    padding: 24, boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    maxHeight: "90vh", overflowY: "auto",
  },
  header: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", marginBottom: 20,
  },
  closeBtn: {
    background: "none", border: "none",
    fontSize: 18, cursor: "pointer", color: "#888",
    padding: "0 4px",
  },
  notice: {
    borderRadius: 8, padding: "10px 14px",
    marginBottom: 16, fontSize: 14,
  },
  form: {
    background: "#f8f8f6", borderRadius: 10,
    padding: 16, marginBottom: 20,
    display: "flex", flexDirection: "column", gap: 10,
  },
  formLabel: {
    fontSize: 13, fontWeight: 600, color: "#444",
    marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em",
  },
  input: {
    padding: "9px 12px", borderRadius: 8,
    border: "1px solid #ddd", fontSize: 14,
    outline: "none", width: "100%", boxSizing: "border-box",
  },
  assignBtn: (disabled) => ({
    padding: "10px 0", borderRadius: 8,
    background: disabled ? "#d0d0d0" : "#0093dd",
    color: "#fff", border: "none",
    fontSize: 14, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
  }),
  sectionLabel: {
    fontSize: 12, fontWeight: 600, color: "#888",
    textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10,
  },
  dropdownList: {
    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
    background: "#fff", border: "1px solid #ddd", borderRadius: 8,
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 100,
    maxHeight: 220, overflowY: "auto",
  },
  dropdownItem: {
    padding: "9px 12px", cursor: "pointer",
    borderBottom: "1px solid #f0f0f0",
    transition: "background 0.1s",
  },
  list: { display: "flex", flexDirection: "column", gap: 8 },
  row: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", background: "#f8f8f6",
    borderRadius: 8, padding: "10px 14px",
  },
  removeBtn: {
    background: "none", border: "1px solid #e0a0a0",
    borderRadius: 6, padding: "4px 10px",
    fontSize: 12, cursor: "pointer", color: "#8B2020",
  },
};
